import React, { useEffect, useState } from 'react';
import ReportTabs from './Report/ReportTabs';
import styled from 'styled-components/macro';
import Insights from 'components/Project/Reports/DefaultReports/Insights/Insights';
import Results from 'components/Project/Reports/DefaultReports/Results/Results';
import Specification from 'components/Project/Reports/DefaultReports/Specification/Specification';
import { Select, Typography } from '@deeptrue-frontend/ui';
import StoreProvider from 'components/StoreProvider/StoreProvider';
import { compose } from 'ramda';
import Files from './Report/Files';
const { connect, selectors, actions } = StoreProvider;

export enum Tabs {
  'reportRecommendations' = -1,
  'results' = 0,
  'specification',
  'files',
}

type Section = 'results' | 'specification' | 'reportRecommendations' | 'files';

export type ReportData = {
  default_language: 'en' | 'de' | 'fr' | 'it';
  description?: string;
  report: {
    results: any[];
    specification: any;
    reportRecommendations: any[];
    files: any[];
  };
  sections: ['results', 'specification', 'reportRecommendations', 'files'];
  title?: string;
};

interface Props {
  report: ReportData;
  setLanguage: (lang: string) => void;
  linkId: string;
  organization: string;
}

const Container = styled.div`
  display: flex;
  width: 100%;
  flex-direction: column;
  height: calc(100% - 4rem);
  overflow: hidden;
  margin-bottom: 2rem;
  position: relative;
`;

const LanguageSwitcher = styled.div`
  position: absolute;
  top: 1rem;
  right: 1rem;
`;

const Title = styled(Typography)`
  font-size: 2rem;
  font-weight: 500;
  color: #253858;
  padding: 1.5rem 1.5rem 0;
`;

const Description = styled(Typography)`
  font-size: 1rem;
  font-weight: 400;
  color: #253858;
  padding: 0 1.5rem 1.5rem;
`;

const SharedReport = ({ report, setLanguage, linkId, organization }: Props) => {
  const [selectedTab, changeTab] = useState(
    report.sections.includes('results') ? 0 : Tabs[report.sections[0]]
  );
  const [selectedSection, setSection] = useState<string | undefined>();
  const choosenTab = Tabs[selectedTab] as Section;
  const dataToDisplay = report ? report.report[choosenTab] : [];

  useEffect(() => {
    setLanguage(report.default_language);
    // eslint-disable-next-line
  }, [report.default_language]);

  const isResultsTabAvailable = report?.sections.includes('results');

  return (
    <Container>
      {report.title && <Title>{report.title}</Title>}
      {report.description && <Description>{report.description}</Description>}
      <LanguageSwitcher>
        <Select
          onChange={(_name: any, val: any) => setLanguage(val)}
          name="langSwitch"
          direction="bottom"
          value={report.default_language}
          options={[
            {
              value: 'en',
              label: 'English',
            },
            {
              value: 'fr',
              label: 'Français',
            },
            {
              value: 'de',
              label: 'Deutsch',
            },
            {
              value: 'it',
              label: 'Italiano',
            },
          ]}
        />
      </LanguageSwitcher>
      <ReportTabs
        selectedTab={selectedTab}
        changeTab={changeTab}
        tabs={report.sections}
      />
      {selectedTab === Tabs.reportRecommendations && (
        <Insights
          dataToDisplay={dataToDisplay}
          setSection={setSection}
          selectedSection={selectedSection}
          changeTab={changeTab}
          disableGoToResults={!isResultsTabAvailable}
        />
      )}

      {selectedTab === Tabs.results && (
        <Results
          setSection={setSection}
          selectedSection={selectedSection}
          dataToDisplay={dataToDisplay}
        />
      )}
      {selectedTab === Tabs.specification && (
        <Specification data={dataToDisplay} />
      )}
      {selectedTab === Tabs.files && (
        <Files
          linkId={linkId}
          organization={organization}
          files={report?.report?.files || []}
        />
      )}
    </Container>
  );
};

export default compose(
  connect(
    (state) => ({
      language: selectors.intlSelectors.language(state),
    }),
    {
      setLanguage: actions.intlActions.setLanguage,
    }
  )
)(SharedReport);
